import React, { useEffect, useRef, useState } from "react";
import { useTable, useFilters, useSortBy, usePagination, useResizeColumns, useBlockLayout, useRowSelect, useGroupBy, useExpanded, Cell, Column } from "react-table";
import { TextInput } from "../TextInput";
import './Grid.scss';
import {Icon} from '../../components/Icon'
import { Form } from "react-bootstrap";
import { Checkbox } from "../..";
import { useDispatch, useSelector } from "react-redux";


type GridProps = {
  columns: any;
  data: any;
  hiddenColumns?: string[];
  sortDefault?: any[];
  showFilters?: boolean;
  highlightColumn?: string;
  selectedColumn?: (selected: boolean, columnValue: string) => void;
  id?:string,
  checkboxColumWidth?:string,
  groupByColumns?:string[],
  columnsGroupOn?:string[];
  disableSorting? : boolean;
  paginationDisabled? :boolean;
  zindexHeader?:number;
  grdTableContainerClass? : string;
  grdTabelClass? : string;
  retainGridState? : boolean;
  defaultPageSize? : number;
  grdFilterInputClassName? : string;
  grdThClassName? : string;
  onSelectionChange?: (count: number) => void;
  navigateToFirstPage? : boolean;
  onNavigateToFirstPage? : () => void;
  enableDefaultSelection?: boolean; 
  enableMandatorySelection?: boolean;
  disableSelection?: boolean;
  maxSelectionCount?: number;
  onMaxSelectionReached?: () => void;
  isRowSelectable?: (row: any) => boolean;
};

export default React.forwardRef(function Grid({ columns, data, hiddenColumns =[], sortDefault, showFilters, highlightColumn, selectedColumn,id,checkboxColumWidth, groupByColumns = [], columnsGroupOn =[], disableSorting, paginationDisabled, zindexHeader, grdTableContainerClass, grdTabelClass, retainGridState, defaultPageSize, grdFilterInputClassName, grdThClassName, onSelectionChange, navigateToFirstPage, onNavigateToFirstPage, enableDefaultSelection, enableMandatorySelection, disableSelection, maxSelectionCount, onMaxSelectionReached, isRowSelectable}: GridProps, ref: React.Ref<any>) {
  const [filterInput, setFilterInput] = useState({});
  const [highlightedRowIndex, setHighlightedRowIndex] = useState<number | null>(null);
  const [clickedRowIndex, setClickedRowIndex] = useState<number | null>(null);
  const onMaxSelectionReachedRef = useRef(onMaxSelectionReached);
  onMaxSelectionReachedRef.current = onMaxSelectionReached;
  const userDetails = useSelector((state: any) => state.userContextSlice);
  const upArrow = <Icon icon='up-arrow' />
  const downArrow = <Icon icon='down-arrow' />
    const upArrowIndicator = <Icon icon='up-indication' />
  const downArrowIndicator = <Icon icon='down-indication' />
  const handleRowClick = (pageIndex: number, localIndex: number) => {
    const globalIndex = pageIndex * pageSize + localIndex;
    const originalRow = rows[globalIndex].original;
    const columnValue = originalRow[highlightColumn];

    if (clickedRowIndex === globalIndex) {
      if (enableMandatorySelection) {
        return;
      }
      else {       
      setHighlightedRowIndex(null);
      setClickedRowIndex(null);
      if (selectedColumn) selectedColumn(false, "");
      }
    } else {
      setHighlightedRowIndex(globalIndex);
      setClickedRowIndex(globalIndex);
      if (selectedColumn) selectedColumn(true, columnValue);
    }
  };

  const defaultColumn = React.useMemo(
    () => ({
      //  width:'100%'
      width: '155',
      // minWidth: 30,
      // width: 100,
      // maxWidth: 400
     // overflow:'visible'
    }),
    []
  );

  React.useImperativeHandle(ref, () => ({
    getSelectedRows: () => {
      const selectedRows = rows.filter((row) => row.isSelected);
      return selectedRows;
    },
    getFilteredRowsForExport: () => rows.map(row => row.original),
    gotoPage,
    clearSelection: () => toggleAllRowsSelected(false)
  }));

  const updatedHiddenColumns = React.useMemo(() => {
    const shouldHideExpandedColumn  = groupByColumns.length ===0;
    return shouldHideExpandedColumn ? [...hiddenColumns, 'expanded'] : hiddenColumns;
  },[groupByColumns,hiddenColumns])

  const renderPagination = () => (
    <div className='py-3'>
    <button className=' btn btn-primary btn-sm searchBtnMargin labelStyle' onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
      {"<<"}
    </button>{" "}
    <button className=' btn btn-primary btn-sm searchBtnMargin labelStyle' onClick={() => previousPage()} disabled={!canPreviousPage}>
      Previous
    </button>{" "}
    <button className=' btn btn-primary btn-sm searchBtnMargin labelStyle' onClick={() => nextPage()} disabled={!canNextPage}>
      Next
    </button>{" "}
    <button className=' btn btn-primary btn-sm searchBtnMargin labelStyle' onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
      {">>"}
    </button>{" "}
    <span className='labelStyle'>
      Page{" "}
      <strong>
        {pageIndex + 1} of {pageOptions.length}
      </strong>{" "}
    </span>
    <span className='labelStyle'>
      | Go to page:{" "}
      <input
        type="number"
        defaultValue={pageIndex + 1}
        max={pageOptions.length}
        min={1}
        onChange={(e) => {
          const pageNumber = e.target.value
            ? Number(e.target.value) - 1
            : 0;
          gotoPage(pageNumber);
        }}
        className='labelStyle pagingSelect nopadding'
        style={{ width: "50px" }}
      />
    </span>{" "}
    <select
      className='labelStyle'
      value={pageSize}
      onChange={(e) => setPageSize(Number(e.target.value))}
    >
      {[10, 20, 50].map((pageSize) => (
        <option key={pageSize} value={pageSize}>
          Show {pageSize}
        </option>
      ))}
    </select>
  </div>
  );

  const [gridState, setGridState] = useState({ pageIndex: 0, pageSize : defaultPageSize ? defaultPageSize : 10, hiddenColumns: updatedHiddenColumns, sortBy: sortDefault, groupBy:groupByColumns});

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state,
    gotoPage,
    pageCount,
    setPageSize,
    prepareRow,
    resetResizing,
    setFilter,
    setGroupBy,
    toggleAllRowsSelected,
    toggleRowSelected,
  } = useTable(
    {
      columns,
      defaultColumn,
      data,
      initialState: gridState,
      autoResetSelectedRows: false,
      stateReducer: (newState, action, prevState) => {
        if (isRowSelectable && (action.type === 'toggleAllPageRowsSelected' || action.type === 'toggleAllRowsSelected')) {
          const prevCount = Object.keys(prevState.selectedRowIds).length;
          const newCount = Object.keys(newState.selectedRowIds).length;
          if (newCount > prevCount) {
            const filtered = {};
            Object.keys(newState.selectedRowIds).forEach(id => {
              const rowData = data[Number(id)];
              if (rowData && isRowSelectable(rowData)) {
                filtered[id] = true;
              }
            });
            newState = { ...newState, selectedRowIds: filtered };
          }
        }
        if (maxSelectionCount !== undefined && (action.type === 'toggleRowSelected' || action.type === 'toggleAllPageRowsSelected' || action.type === 'toggleAllRowsSelected')) {
          const count = Object.keys(newState.selectedRowIds).length;
          if (count > maxSelectionCount) {
            if (onMaxSelectionReachedRef.current) {
              onMaxSelectionReachedRef.current();
            }
            return { ...newState, selectedRowIds: prevState.selectedRowIds };
          }
        }
        return newState;
      },
    },
    useFilters,
    useResizeColumns,
    useBlockLayout,
    useGroupBy,
    !disableSorting && useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push(columns => [
        {
          
          id:'expanded',
          width:50,
          Header :  ({getToggleAllRowsExpandedProps}) => (
            <span {...getToggleAllRowsExpandedProps()}>
              {page.length ? (page[0].isExpanded? upArrow : downArrow ):null}
            </span>
          ),
          Cell: ({row}) => (
            row.canExpand? (
            <span {...row.getToggleRowExpandedProps()}>
              {row.isExpanded ? upArrow  : downArrow}
            </span>
            ) : null
          ),
        },
        {
          id: 'selection',
          width: checkboxColumWidth!==undefined?checkboxColumWidth:'50',
          Header: ({ getToggleAllPageRowsSelectedProps, page: headerPage, selectedFlatRows }) => {
            const toggleProps = getToggleAllPageRowsSelectedProps();
            let overrideProps = toggleProps;
            if (isRowSelectable) {
              const selectableRows = headerPage.filter(row => isRowSelectable(row.original));
              const selectedSelectableRows = selectableRows.filter(row => row.isSelected);
              const allSelectableSelected = selectableRows.length > 0 && selectedSelectableRows.length === selectableRows.length;
              const someSelected = selectedSelectableRows.length > 0 && !allSelectableSelected;
              overrideProps = { ...toggleProps, checked: allSelectableSelected, indeterminate: someSelected };
            }
            return (
            <div>
              <div onClick={(event) => event.stopPropagation()}>
                <Form.Check className="mx-1 mt-2">
                  <Form.Check.Input
                    type="checkbox"
                    className="cstGridCheckBox"
                    {...overrideProps}
                    onClick={(event) => event.stopPropagation()}
                    disabled={disableSelection}
                  />
                  <Form.Check.Label onClick={(event) => event.stopPropagation()} />
                </Form.Check>
              </div>
            </div>
            );
          },
          hideColumnFilter: true,
          Cell: ({ row }) => {
            const isRowDisabled = disableSelection || (isRowSelectable && !isRowSelectable(row.original));
            return (
            <div className="ps-1">
              <Form.Check className="mx-2">
                <Form.Check.Input
                  type="checkbox"
                  className="cstGridCheckBox"
                  {...row.getToggleRowSelectedProps()}
                  onClick={(event) => event.stopPropagation()}
                  disabled={isRowDisabled}
                />
                <Form.Check.Label onClick={(event) => event.stopPropagation()} />
              </Form.Check>

            </div>
            );
          },
        },
        ...columns
      ])
    }
  );

  useEffect(() => {

    if (onNavigateToFirstPage){
      onNavigateToFirstPage();
      gotoPage(0);
    }

  },[navigateToFirstPage]);

    const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilter(name, value);
    setFilterInput({
      ...filterInput,
      [name]: value,
    });
    };

    const handleCheckBox = e => {
        const { checked } = e.target;
        setFilter('onHold', checked);
        setFilterInput({
            ...filterInput,
            ['onHold']: checked,
        });
    }

  const { filters, sortBy, pageIndex, pageSize, selectedRowIds } = state;

    useEffect(() => {
    if (retainGridState) {
      setGridState((grdState) => {
        return {...grdState, ...state};
      });
    }
    }, [filters, sortBy, pageIndex, pageSize]);
    useEffect(() => {
    if (!isRowSelectable) {
      return;
    }
 
    const staleSelectedRowIds = rows
      .filter((row) => row.isSelected && !isRowSelectable(row.original))
      .map((row) => row.id);
 
    if (staleSelectedRowIds.length > 0) {
      staleSelectedRowIds.forEach((rowId) => toggleRowSelected(rowId, false));
    }
  }, [rows, isRowSelectable, toggleRowSelected]);
  
    useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(Object.keys(selectedRowIds).length);
    }
  }, [selectedRowIds]);

  useEffect(() => {
    if (userDetails?.userContext?.pageSize && userDetails.userContext.pageSize > 0) {
      setPageSize(Number(userDetails.userContext.pageSize));
    }
  }, [userDetails?.userContext?.pageSize, setPageSize]);

  const rowsToDisplay = paginationDisabled? rows : page

  useEffect(() => {
    if (groupByColumns.length>0) {
      setGroupBy(groupByColumns)
    }
  },[setGroupBy, groupByColumns]
);

    useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--table-header-height', showFilters ? '60px' : '30px')
  }, [showFilters]);

    useEffect(() => {
    setHighlightedRowIndex(null)
  }, [data]);

    useEffect(() => {
    if (enableDefaultSelection && data.length > 0) {
      handleRowClick(0, 0); // Select the first row manually
    }
  }, [data, enableDefaultSelection]);

    useEffect(() => {
    if (enableMandatorySelection && data.length > 0) {
      handleRowClick(0, 0);
    }
  }, [data, enableMandatorySelection]);


  return (
    <>
      {/* <div className="cstGrid container-fluid">
      <div className='row'>
        <div className='col-lg-12 col-ld-12 col-sm-12 col-sx-12'> */}

      <div className={`table-container ${grdTableContainerClass ? grdTableContainerClass : ''}`}>
        <table id={id} {...getTableProps()} className={`table ${grdTabelClass ? grdTabelClass : ''}`}>
          <thead className="fixedHeader">
          {/* style={{zIndex:zindexHeader}}> */}
                      {headerGroups.map(headerGroup => {
              const { key: hgKey, ...hgProps } = headerGroup.getHeaderGroupProps();
              return <tr key={hgKey} {...hgProps}>
                              {headerGroup.headers.map(column => {
                                  const { key: colKey, ...colProps } = column.getHeaderProps(!disableSorting && column.getSortByToggleProps());
                                  return <th
                                      key={colKey}
                                      {...colProps}
                                      className={`${column.isSorted && !disableSorting
                                          ? (column.isSortedDesc ? "sort-desc" : "sort-asc") : ""} 
                      ${grdThClassName ? grdThClassName : ""} 
                      ${column['headerClassName'] ? column['headerClassName'] : ""}`}

                                  >
                                      {column.render("Header")}

                                      {/* show up / down arrow for sorted column */}
                                      {!disableSorting && column.isSorted ? (
                                          column.isSortedDesc ? <span className="sort-icons">{downArrowIndicator}</span> : <span className="sort-icons">{upArrowIndicator}</span>
                                      ) : null}

                    <div
                      {...column.getResizerProps()}
                      className={`resizer ${column.isResizing ? "isResizing" : ""
                        }
                  `}
                            onClick={(event) => event.stopPropagation()} />
                        {showFilters && column.canFilter && (!(column as any)?.hideColumnFilter) ?
                            column.sortType == 'bool' ? 
                                (<>
                                    <div onClick={(event) => event.stopPropagation()}>
                                        <Checkbox
                                            style={{ height: "30px", justifyContent: "center", display: "flex" }}
                                            className={`${grdFilterInputClassName ? grdFilterInputClassName : ''} mt-2 mb-0`}
                                            name={column.id}
                                            checked={filterInput[column.id]? true: false}
                                            onChange={handleCheckBox}
                                            />
                                    </div>
                                </>)
                            :
                            (<>
                        <div onClick={(event) => event.stopPropagation()}>
                          <TextInput
                            style={{ height: "30px" }}
                            className={`${grdFilterInputClassName ? grdFilterInputClassName : ''} mt-2 mb-0`}
                            name={column.id}
                            value={filterInput[column.id] || ''}
                            onChange={handleFilterChange} />
                        </div>
                      </>
                    ) : null}
                  </th>
                })}
              </tr>
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rowsToDisplay.map((row, localIndex) => {
              prepareRow(row);
              const globalIndex = paginationDisabled ? localIndex : pageIndex * pageSize + localIndex;
              const isHighlighted = globalIndex === highlightedRowIndex;
              const { key, ...rowProps } = row.getRowProps();
              return (
                <React.Fragment key={`row-${row.id}-${localIndex}`}>
                <tr
                  {...rowProps}
                  className={`
                    ${row.isSelected && highlightColumn !== undefined ? "selected-row" : ""}  
                  `}
                  onClick={(event) => {
                    // handleRowClick(i)
                      handleRowClick(pageIndex, localIndex);
                      event.stopPropagation();
                    }}
                  >
                    {row.cells.map((cell, cellIndex) => {
                      const isColumnGroupByOn = columnsGroupOn.includes(cell.column.id);
                      const originalValue = row.subRows && row.subRows[0] && row.subRows[0].original[cell.column.id];
                      let tdProps = {};

                      if (cell
                        && cell.row
                        && cell.row.original
                        && cell.column
                        && cell.row.original['enableGridRowEditing']
                        && cell.column['CellEditTDProperties']) {
                        tdProps = cell.column['CellEditTDProperties'];
                      }

                      const { key: cellKey, ...cellProps } = cell.getCellProps();
                      return (
                        <td key={cellKey || `cell-${row.id}-${cellIndex}`} {...cellProps} className={`${cell.column['cellClassName'] ? cell.column['cellClassName'] : ""} 
            ${isHighlighted && highlightColumn !== undefined ? " highlighted-row" : ""}`} {...tdProps}>

                          {cell.column.id === 'expanded' ? (
                            cell.render("Cell")
                          ) : (
                            (row.canExpand ? (
                              isColumnGroupByOn ? originalValue : ''
                            ) : (
                              isColumnGroupByOn ? '' : cell.render("Cell")
                            )
                            ))}
                        </td>
                      );
                    })}
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

      </div>
      {/* </div>
      </div>
    </div> */}
        {!paginationDisabled && renderPagination()}

    </>
  );
})